import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ChevronRight, Mail, BadgeCheck, Unlock, Lock, History, Pencil, Ban, Plus, AlertTriangle, RefreshCw, KeyRound, Clock, CalendarClock, FileText, FileSpreadsheet, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { TimePicker } from "@/components/TimePicker";
import { DatePicker } from "@/components/DatePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SERVICES, slugFromKey, fmtDuration, fmtDateIt, ENTITY_KINDS } from "@/lib/services";
import { fmtNum } from "@/lib/format";
import { StampInfo } from "@/components/StampInfo";

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const ENTITY_KIND_FALLBACK = { assistenza_scolastica: "school_user", adm: "school_user", nido: "school_user", pre_scuola: "operative_site", post_scuola: "operative_site", centro_estivo: "operative_site", spazio_compiti: "operative_site", blue_box: "appalto" };

function fmtDT(iso) {
  try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  const map = {
    archiviato: { t: "Archiviato", c: "bg-slate-200 text-slate-600" },
    riaperto_ufficio: { t: "Riaperto", c: "bg-amber-100 text-amber-700" },
    aperto: { t: "Aperto", c: "bg-success/10 text-success" },
  };
  const s = map[status] || map.archiviato;
  return <Badge className={`${s.c} hover:${s.c} border-0 text-[10px]`}>{s.t}</Badge>;
}

function EditWorkerDialog({ worker, onClose, onSaved }) {
  const isLavoratore = worker.profilo_accesso === "LAVORATORE";
  const [form, setForm] = useState({
    nome: worker.nome, cognome: worker.cognome, ruolo_professionale: worker.ruolo_professionale,
    monte_ore_settimanale_contrattuale: worker.monte_ore_settimanale_contrattuale ?? "",
  });
  const [appalti, setAppalti] = useState(null); // opzioni nel perimetro dell'Ufficio
  const [selected, setSelected] = useState([]); // id selezionati (solo perimetro)
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isLavoratore) return;
    api.get("/my-appalti").then((r) => {
      setAppalti(r.data);
      const optIds = new Set(r.data.map((a) => a.id));
      setSelected((worker.appalto_ids || []).filter((id) => optIds.has(id)));
    }).catch(() => setAppalti([]));
  }, [isLavoratore, worker.appalto_ids]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const raw = form.monte_ore_settimanale_contrattuale;
    const ore = raw === "" || raw == null ? null : Number(String(raw).replace(",", "."));
    try {
      await api.put(`/workers/${worker.id}`, { ...form, monte_ore_settimanale_contrattuale: Number.isFinite(ore) ? ore : null });
      if (isLavoratore) {
        await api.patch(`/workers/${worker.id}/appalti`, { appalto_ids: selected });
      }
      toast.success("Dati aggiornati");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Modifica dati lavoratore</DialogTitle>
          <DialogDescription>Nome, cognome, ruolo, monte ore e appalti associati.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && <div data-testid="edit-worker-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome</Label><Input required value={form.nome} data-testid="edit-nome" onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Cognome</Label><Input required value={form.cognome} data-testid="edit-cognome" onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Ruolo professionale</Label><Input required value={form.ruolo_professionale} data-testid="edit-ruolo" onChange={(e) => setForm({ ...form, ruolo_professionale: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Monte ore settimanale contrattuale</Label>
            <Input inputMode="decimal" value={form.monte_ore_settimanale_contrattuale} data-testid="edit-monte-ore" placeholder="Es. 38,5"
              onChange={(e) => setForm({ ...form, monte_ore_settimanale_contrattuale: e.target.value })} /></div>
          {isLavoratore && (
            <div className="space-y-1.5">
              <Label>Appalti associati</Label>
              <p className="text-xs text-slate-400 -mt-0.5">Un lavoratore attivo deve avere almeno un appalto. Vedi e gestisci solo gli appalti del tuo perimetro.</p>
              {appalti === null ? (
                <p className="text-sm text-slate-500 p-2">Caricamento appalti…</p>
              ) : appalti.length === 0 ? (
                <p className="text-sm text-slate-500 p-2">Nessun appalto attivo nel tuo perimetro.</p>
              ) : (
                <div className="rounded-lg border border-slate-200 divide-y max-h-44 overflow-y-auto" data-testid="edit-appalti-list">
                  {appalti.map((a) => {
                    const checked = selected.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-2.5 p-2.5 cursor-pointer hover:bg-slate-50">
                        <Checkbox checked={checked} data-testid={`edit-appalto-${a.id}`} onCheckedChange={() => toggle(a.id)} />
                        <span className="text-sm text-slate-700">{a.nome}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={saving} data-testid="edit-worker-submit" className="w-full rounded-lg h-11 font-semibold">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function OfficeWorkerFolder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [months, setMonths] = useState([]);
  const [sel, setSel] = useState(null); // {year, month, status}
  const [registers, setRegisters] = useState([]);
  const [officeCanEdit, setOfficeCanEdit] = useState(false);
  const [openReg, setOpenReg] = useState(null); // register detail data
  const [dialog, setDialog] = useState(null); // {type, ...}
  const [emailBusy, setEmailBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [exportAppalti, setExportAppalti] = useState([]);
  const [exporting, setExporting] = useState("");
  const [indirect, setIndirect] = useState([]);
  const [indirectEdit, setIndirectEdit] = useState(null);
  const [indirectCancel, setIndirectCancel] = useState(null);

  const exportFile = async (kind, appaltoId, token) => {
    if (!sel) return;
    setExporting(token);
    try {
      let url = `/office/export/${kind}?worker_id=${id}&year=${sel.year}&month=${sel.month}`;
      if (kind === "excel" && appaltoId) url += `&appalto_id=${appaltoId}`;
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `${kind === "excel" ? "LibroPresenze" : "Registro"}_${sel.year}_${sel.month}.${kind === "excel" ? "xlsx" : "pdf"}`;
      const objurl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = objurl; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(objurl);
      toast.success(kind === "excel" ? "Libro Presenze pronto" : "Registro pronto");
    } catch {
      toast.error("Esportazione non riuscita. Riprova.");
    } finally { setExporting(""); }
  };

  const sendEmail = async () => {
    setEmailBusy(true);
    try {
      if (worker.attivato) await api.post(`/workers/${id}/send-reset`);
      else await api.post(`/workers/${id}/resend-activation`);
      toast.success(worker.attivato ? "E-mail di reimpostazione password inviata." : "E-mail di attivazione inviata.");
      await loadMonths();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setEmailBusy(false); }
  };

  const loadMonths = useCallback(async () => {
    const [w, m] = await Promise.all([api.get(`/office/workers/${id}`), api.get(`/office/workers/${id}/months`)]);
    setWorker(w.data); setMonths(m.data.months);
  }, [id]);

  useEffect(() => { loadMonths(); }, [loadMonths]);

  const loadIndirect = useCallback(async (y, mo) => {
    try {
      const { data } = await api.get(`/office/indirect-hours?worker_id=${id}&year=${y}&month=${mo}`);
      setIndirect(data || []);
    } catch { setIndirect([]); }
  }, [id]);

  const openMonth = async (y, mo) => {
    const { data } = await api.get(`/office/workers/${id}/registers?year=${y}&month=${mo}`);
    setSel({ year: y, month: mo, status: data.status });
    setRegisters(data.registers); setOfficeCanEdit(data.office_can_edit); setOpenReg(null);
    api.get(`/office/export/appalti?worker_id=${id}&year=${y}&month=${mo}`).then((r) => setExportAppalti(r.data || [])).catch(() => setExportAppalti([]));
    loadIndirect(y, mo);
  };

  const openRegister = async (st, eid, kind) => {
    const kq = kind ? `&entity_kind=${kind}` : "";
    const { data } = await api.get(`/office/register?worker_id=${id}&service_type=${st}&entity_id=${eid}&year=${sel.year}&month=${sel.month}${kq}`);
    setOpenReg(data);
  };

  const refreshReg = async () => {
    if (openReg) await openRegister(openReg.service_type, openReg.entity.id, openReg.entity_kind);
    await loadMonths();
    if (sel) { const { data } = await api.get(`/office/workers/${id}/registers?year=${sel.year}&month=${sel.month}`); setRegisters(data.registers); setOfficeCanEdit(data.office_can_edit); setSel((s) => ({ ...s, status: data.status })); }
  };

  const monthAction = async (type, reason) => {
    try {
      await api.post(`/office/months/${type}`, { worker_id: id, year: sel.year, month: sel.month, reason });
      toast.success(type === "reopen" ? "Mese riaperto" : "Mese richiuso");
      setDialog(null);
      await openMonth(sel.year, sel.month);
      await loadMonths();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-10">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 py-6">
        <button onClick={() => (openReg ? setOpenReg(null) : sel ? setSel(null) : navigate("/ufficio"))} data-testid="folder-back"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> {openReg ? "Registri" : sel ? "Mesi" : "Lavoratori"}
        </button>

        {!worker ? <p className="text-sm text-slate-500">Caricamento…</p> : (
          <>
            {/* Dati generali */}
            {!sel && (
              <Card className="rounded-2xl border-slate-200 shadow-sm mb-5" data-testid="worker-general-data">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-lg shrink-0">
                      {(worker.nome[0] || "") + (worker.cognome[0] || "")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h1 className="font-heading text-xl font-bold text-slate-900">{worker.nome} {worker.cognome}</h1>
                      <p className="text-sm text-slate-500">{worker.ruolo_professionale}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <p className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" /> {worker.email}</p>
                    <p className="flex items-center gap-2 text-slate-600"><BadgeCheck className="h-4 w-4 text-slate-400" />
                      {!worker.attivato ? "Da attivare" : worker.account_attivo ? "Account attivo" : "Account disattivato"}</p>
                    {worker.created_at && <p className="flex items-center gap-2 text-slate-600"><CalendarClock className="h-4 w-4 text-slate-400" /> Creato: {fmtDT(worker.created_at)}</p>}
                    {worker.activated_at && <p className="flex items-center gap-2 text-slate-600"><BadgeCheck className="h-4 w-4 text-slate-400" /> Attivato: {fmtDT(worker.activated_at)}</p>}
                    {worker.last_login && <p className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-slate-400" /> Ultimo accesso: {fmtDT(worker.last_login)}</p>}
                    {worker.monte_ore_settimanale_contrattuale != null && (
                      <p className="flex items-center gap-2 text-slate-600" data-testid="worker-monte-ore"><Clock className="h-4 w-4 text-slate-400" /> Monte ore settimanale contrattuale: {fmtNum(worker.monte_ore_settimanale_contrattuale)} h</p>
                    )}
                    {worker.anomalie_non_giustificate != null && (
                      <p className="flex items-center gap-2 text-slate-600" data-testid="worker-anomaly-count">
                        <AlertTriangle className={`h-4 w-4 ${worker.anomalie_non_giustificate > 0 ? "text-destructive" : "text-slate-400"}`} />
                        Errori di timbratura non giustificati: <b className={worker.anomalie_non_giustificate > 0 ? "text-destructive" : ""}>{worker.anomalie_non_giustificate}</b>
                      </p>
                    )}
                  </div>
                  {worker.ultima_email && (
                    <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600" data-testid="last-email-info">
                      <span className="font-semibold">Ultima e-mail:</span> {worker.ultima_email.tipo === "attivazione" ? "Attivazione" : "Reset password"} · {fmtDT(worker.ultima_email.data_ora)} ·
                      <span className={worker.ultima_email.esito === "sent" ? "text-success ml-1" : "text-destructive ml-1"}>
                        {worker.ultima_email.esito === "sent" ? "inviata" : worker.ultima_email.esito === "not_configured" ? "non configurata" : "fallita"}
                      </span>
                    </div>
                  )}
                  {!worker.email_configured && (
                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">Servizio e-mail non configurato.</div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-worker-btn"
                      className="rounded-lg text-slate-700"><Pencil className="h-4 w-4 mr-1.5" /> Modifica dati</Button>
                    {!worker.attivato ? (
                      <Button size="sm" variant="outline" onClick={sendEmail} disabled={emailBusy} data-testid="folder-resend-activation"
                        className="rounded-lg text-amber-700 border-amber-300 hover:bg-amber-50">
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${emailBusy ? "animate-spin" : ""}`} /> Reinvia e-mail di attivazione
                      </Button>
                    ) : worker.account_attivo ? (
                      <Button size="sm" variant="outline" onClick={sendEmail} disabled={emailBusy} data-testid="folder-send-reset"
                        className="rounded-lg text-primary border-primary/30 hover:bg-primary/5">
                        <KeyRound className={`h-4 w-4 mr-1.5 ${emailBusy ? "animate-spin" : ""}`} /> Invia e-mail di reimpostazione password
                      </Button>
                    ) : (
                      <p className="text-xs text-slate-400">Account disattivato: riattivalo dal pannello Lavoratori per inviare e-mail operative.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mesi */}
            {!sel && (
              <>
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">Mesi</h2>
                <div className="space-y-2.5" data-testid="folder-months">
                  {months.map((m) => (
                    <button key={`${m.year}-${m.month}`} onClick={() => openMonth(m.year, m.month)} data-testid={`folder-month-${m.year}-${m.month}`}
                      className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800 capitalize">{MONTHS[m.month - 1]} {m.year}</p>
                          <StatusBadge status={m.status} />
                          {m.is_current && <Badge className="bg-[#E0F2FE] text-primary hover:bg-[#E0F2FE] border-0 text-[10px]">Corrente</Badge>}
                        </div>
                        <p className="text-sm text-slate-500">Totale ore valide: {fmtDuration(m.total_minutes)}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Registri del mese */}
            {sel && !openReg && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-lg font-semibold text-slate-800 capitalize">{MONTHS[sel.month - 1]} {sel.year}</h2>
                    <StatusBadge status={sel.status} />
                  </div>
                  {sel.status === "riaperto_ufficio" ? (
                    <Button size="sm" variant="outline" data-testid="close-month-btn" onClick={() => setDialog({ type: "close" })}
                      className="rounded-lg text-slate-700"><Lock className="h-4 w-4 mr-1.5" /> Richiudi mese</Button>
                  ) : sel.status === "archiviato" ? (
                    <Button size="sm" variant="outline" data-testid="reopen-month-btn" onClick={() => setDialog({ type: "reopen" })}
                      className="rounded-lg text-amber-700 border-amber-300"><Unlock className="h-4 w-4 mr-1.5" /> Riapri mese</Button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="office-export-buttons">
                  <Button variant="outline" size="sm" onClick={() => exportFile("pdf", null, "pdf")} disabled={!!exporting || registers.length === 0}
                    data-testid="office-export-pdf-btn" className="rounded-lg font-semibold">
                    <FileText className="h-4 w-4 mr-1.5 text-red-600" /> {exporting === "pdf" ? "Attendi…" : "Registro"}
                  </Button>
                  {exportAppalti.map((a) => {
                    const token = `excel-${a.id}`;
                    return (
                      <Button key={a.id} variant="outline" size="sm" onClick={() => exportFile("excel", a.id, token)} disabled={!!exporting}
                        data-testid={`office-export-libro-${a.id}`} className="rounded-lg font-semibold">
                        <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> {exporting === token ? "Attendi…" : `Libro Presenze – ${a.nome}`}
                      </Button>
                    );
                  })}
                </div>
                {sel.status === "riaperto_ufficio" && (
                  <div className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="reopened-banner">
                    Mese riaperto dall'Ufficio: solo l'Ufficio può intervenire. Il lavoratore non può modificarlo.
                  </div>
                )}
                {registers.length === 0 ? (
                  <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm">Nessun registro compilato.</CardContent></Card>
                ) : (
                  <div className="space-y-3" data-testid="folder-registers">
                    {registers.map((r) => {
                      const cfg = SERVICES[slugFromKey(r.service_type)]; const Icon = cfg.icon;
                      return (
                        <button key={`${r.service_type}-${r.entity_id}-${r.role_or_task || ""}`} data-testid={`folder-register-${r.entity_id}${r.role_or_task ? `-${r.role_or_task}` : ""}`} onClick={() => openRegister(r.service_type, r.entity_id, r.entity_kind)}
                          className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg ${cfg.accent} flex items-center justify-center shrink-0`}><Icon className="h-5 w-5 text-primary" /></div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/70">{cfg.short}</p>
                            <p className="font-semibold text-slate-800 truncate">{r.entity_kind === "appalto" ? (r.role_or_task || r.titolo) : `${r.role_or_task ? `${r.role_or_task} · ` : ""}${r.titolo}`}</p>
                          </div>
                          <p className="font-heading font-bold text-primary shrink-0">{fmtDuration(r.total_minutes)}</p>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Ore indirette del mese (consultazione + correzione, nel perimetro Ufficio) */}
                <div className="orbit-area-title" data-testid="office-indirect-section">
                  <h3 className="font-heading text-base font-semibold text-[#4A4470] mb-3 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-[#6D5FB8]" /> Ore indirette
                  </h3>
                  {indirect.length === 0 ? (
                    <Card className="rounded-xl border-[#E5E1F3] shadow-sm bg-[#F7F6FC]"><CardContent className="p-5 text-center text-slate-500 text-sm" data-testid="office-indirect-empty">Nessuna ora indiretta dichiarata in questo mese.</CardContent></Card>
                  ) : (
                    <div className="space-y-2.5" data-testid="office-indirect-list">
                      {indirect.map((r) => {
                        const cancelled = r.status === "cancelled";
                        return (
                        <div key={r.id} data-testid={`office-indirect-${r.id}`}
                          className={`rounded-xl border shadow-sm px-4 py-3 flex items-center gap-3 ${cancelled ? "bg-slate-50 border-slate-200 opacity-70" : "bg-[#F7F6FC] border-[#E5E1F3]"}`}>
                          <div className="h-9 w-9 rounded-lg bg-[#EDEBF7] flex items-center justify-center shrink-0"><ClipboardList className="h-4 w-4 text-[#6D5FB8]" /></div>
                          <div className="min-w-0 flex-1">
                            <p className={`font-semibold leading-tight flex items-center gap-2 ${cancelled ? "text-slate-500 line-through" : "text-slate-800"}`}>
                              {r.indirect_type}
                              {cancelled && <span className="no-underline text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">Annullata</span>}
                            </p>
                            <p className="text-xs text-slate-500 leading-tight flex items-center gap-2 flex-wrap">
                              <span>{fmtDateIt(r.date)}</span>
                              <span>· {r.start_time}–{r.end_time}</span>
                              {r.appalto_nome && <span>· {r.appalto_nome}</span>}
                            </p>
                          </div>
                          <span className={`text-xs font-semibold shrink-0 ${cancelled ? "text-slate-400" : "text-slate-700"}`}>{fmtDuration(r.duration_minutes)}</span>
                          {!cancelled && (
                            <>
                              <Button size="sm" variant="outline" data-testid={`office-indirect-edit-${r.id}`}
                                onClick={() => setIndirectEdit(r)} className="rounded-lg h-8 px-2.5 text-xs shrink-0">
                                <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica
                              </Button>
                              <Button size="sm" variant="outline" data-testid={`office-indirect-cancel-${r.id}`}
                                onClick={() => setIndirectCancel(r)} className="rounded-lg h-8 px-2.5 text-xs shrink-0 text-destructive border-destructive/30 hover:bg-destructive/5">
                                <Ban className="h-3.5 w-3.5 mr-1" /> Annulla
                              </Button>
                            </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
            {openReg && (
              <RegisterDetail data={openReg} canEdit={officeCanEdit} workerId={id}
                onChanged={refreshReg} onHistory={(e) => setDialog({ type: "history", entry: e })}
                onNew={() => setDialog({ type: "create" })} onCorrect={(e) => setDialog({ type: "correct", entry: e })}
                onCancel={(e) => setDialog({ type: "cancel", entry: e })} />
            )}
          </>
        )}
      </main>

      {dialog?.type === "reopen" && <ReasonDialog title="Riapri mese" desc="Indica la motivazione della riapertura. Verrà tracciata nell'audit log." confirmLabel="Riapri mese" testid="reopen" required onClose={() => setDialog(null)} onConfirm={(r) => monthAction("reopen", r)} />}
      {dialog?.type === "close" && <ReasonDialog title="Richiudi mese" desc="Il mese tornerà archiviato. L'operazione è tracciata." confirmLabel="Richiudi mese" testid="close" onClose={() => setDialog(null)} onConfirm={(r) => monthAction("close", r)} />}
      {dialog?.type === "history" && <HistoryDialog entry={dialog.entry} onClose={() => setDialog(null)} />}
      {dialog?.type === "create" && <OfficeEntryDialog mode="create" reg={openReg} workerId={id} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refreshReg(); }} />}
      {dialog?.type === "correct" && <OfficeEntryDialog mode="correct" reg={openReg} entry={dialog.entry} workerId={id} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refreshReg(); }} />}
      {dialog?.type === "cancel" && <ReasonDialog title="Annulla prestazione" desc="La registrazione resterà nello storico, esclusa dai totali." confirmLabel="Conferma annullamento" testid="office-cancel" required danger onClose={() => setDialog(null)} onConfirm={async (r) => { try { await api.post(`/work-entries/${dialog.entry.id}/cancel`, { reason: r }); toast.success("Registrazione annullata"); setDialog(null); refreshReg(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} />}
      {editOpen && <EditWorkerDialog worker={worker} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); loadMonths(); }} />}
      {indirectEdit && worker && (
        <OfficeIndirectEditDialog record={indirectEdit} worker={worker}
          onClose={() => setIndirectEdit(null)}
          onSaved={() => { setIndirectEdit(null); if (sel) loadIndirect(sel.year, sel.month); }} />
      )}
      {indirectCancel && (
        <Dialog open onOpenChange={(o) => !o && setIndirectCancel(null)}>
          <DialogContent className="rounded-2xl" data-testid="office-indirect-cancel-dialog">
            <DialogHeader><DialogTitle className="font-heading">Annullare questa Ora indiretta?</DialogTitle>
              <DialogDescription>Resterà nello storico come "Annullata" e non sarà più conteggiata negli export e nel monte ore.</DialogDescription></DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setIndirectCancel(null)} data-testid="office-indirect-cancel-abort" className="rounded-lg">Annulla</Button>
              <Button data-testid="office-indirect-cancel-confirm" className="rounded-lg bg-destructive hover:bg-destructive/90 text-white"
                onClick={async () => {
                  try { await api.post(`/office/indirect-hours/${indirectCancel.id}/cancel`); toast.success("Ora indiretta annullata"); const s = sel; setIndirectCancel(null); if (s) loadIndirect(s.year, s.month); }
                  catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}>Conferma annullamento</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function OfficeIndirectEditDialog({ record, worker, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [appalti, setAppalti] = useState([]);
  const [form, setForm] = useState({
    appalto_id: record.appalto_id, date: record.date, indirect_type: record.indirect_type,
    start_time: record.start_time, end_time: record.end_time,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/indirect-types").then((r) => setTypes(r.data || [])).catch(() => setTypes([]));
    // Appalti selezionabili = perimetro Ufficio ∩ appalti del lavoratore.
    api.get("/my-appalti").then((r) => {
      const wids = new Set(worker.appalto_ids || []);
      setAppalti((r.data || []).filter((a) => wids.has(a.id)));
    }).catch(() => setAppalti([]));
  }, [worker.appalto_ids]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      await api.patch(`/office/indirect-hours/${record.id}`, form);
      toast.success("Ora indiretta corretta");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="office-indirect-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">Correggi ora indiretta</DialogTitle>
          <DialogDescription>Modifica appalto, data, tipologia e orario. La durata è ricalcolata automaticamente.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && <div data-testid="office-indirect-edit-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="space-y-1.5">
            <Label>Appalto</Label>
            <Select value={form.appalto_id} onValueChange={(v) => setForm((f) => ({ ...f, appalto_id: v }))}>
              <SelectTrigger data-testid="office-indirect-appalto" className="h-11"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DatePicker label="Data" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} testid="office-indirect-date" />
          <div className="space-y-1.5">
            <Label>Tipologia</Label>
            <Select value={form.indirect_type} onValueChange={(v) => setForm((f) => ({ ...f, indirect_type: v }))}>
              <SelectTrigger data-testid="office-indirect-type" className="h-11"><SelectValue placeholder="Tipologia" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TimePicker label="Dalle ore" value={form.start_time} onChange={(v) => setForm((f) => ({ ...f, start_time: v }))} testid="office-indirect-start" />
            <TimePicker label="Alle ore" value={form.end_time} onChange={(v) => setForm((f) => ({ ...f, end_time: v }))} testid="office-indirect-end" />
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="office-indirect-save" className="w-full rounded-lg h-11 font-semibold">{saving ? "Salvataggio…" : "Salva correzione"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RegisterDetail({ data, canEdit, onChanged, onHistory, onNew, onCorrect, onCancel }) {
  const cfg = SERVICES[slugFromKey(data.service_type)]; const Icon = cfg.icon;
  return (
    <div data-testid="office-register-detail">
      <Card className="rounded-2xl border-slate-200 shadow-sm mb-4">
        <CardContent className="p-5 flex items-start gap-3">
          <div className={`h-11 w-11 rounded-xl ${cfg.accent} flex items-center justify-center shrink-0`}><Icon className="h-5 w-5 text-primary" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/70">{cfg.short}</p>
            <h1 className="font-heading text-xl font-bold text-slate-900">{data.entity.titolo}</h1>
            <p className="text-sm text-slate-500">{data.entity.sottotitolo}</p>
          </div>
          <p className="font-heading text-xl font-bold text-primary shrink-0">{fmtDuration(data.total_minutes)}</p>
        </CardContent>
      </Card>

      {canEdit && (
        <Button data-testid="office-new-entry-btn" onClick={onNew} className="w-full rounded-lg h-11 font-semibold mb-4">
          <Plus className="h-4 w-4 mr-2" /> Aggiungi prestazione (Ufficio)
        </Button>
      )}

      <div className="space-y-3" data-testid="office-entries-list">
        {data.entries.map((e) => {
          const cancelled = e.status === "annullata";
          return (
            <Card key={e.id} className={`rounded-xl border-slate-200 shadow-sm ${cancelled ? "opacity-70" : ""}`} data-testid={`office-entry-${e.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold text-slate-800 capitalize ${cancelled ? "line-through" : ""}`}>{fmtDateIt(e.work_date)}</p>
                      {e.corretta && !cancelled && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" /> Corretta</Badge>}
                      {cancelled && <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10 border-0 text-[10px]">Annullata</Badge>}
                    </div>
                    <p className={`text-sm text-slate-500 ${cancelled ? "line-through" : ""}`}>{e.start_time}–{e.end_time}{e.role_or_task && ` · ${e.role_or_task}`}</p>
                  </div>
                  <p className={`font-heading font-bold text-primary shrink-0 ${cancelled ? "line-through" : ""}`}>{fmtDuration(e.duration_minutes)}</p>
                </div>
                <StampInfo entry={e} className="mt-3" />
                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                  {(e.corretta || cancelled) && (
                    <Button variant="outline" size="sm" data-testid={`history-btn-${e.id}`} onClick={() => onHistory(e)} className="rounded-lg text-slate-600">
                      <History className="h-3.5 w-3.5 mr-1.5" /> Cronologia
                    </Button>
                  )}
                  {canEdit && !cancelled && (
                    <>
                      <Button variant="outline" size="sm" data-testid={`office-correct-${e.id}`} onClick={() => onCorrect(e)} className="rounded-lg text-slate-600"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Correggi</Button>
                      <Button variant="outline" size="sm" data-testid={`office-cancel-${e.id}`} onClick={() => onCancel(e)} className="rounded-lg text-destructive border-destructive/30"><Ban className="h-3.5 w-3.5 mr-1.5" /> Annulla</Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ReasonDialog({ title, desc, confirmLabel, testid, required, danger, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        {error && <div data-testid={`${testid}-error`} className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
        <div className="space-y-1.5">
          <Label>Motivazione{required ? "" : " (facoltativa)"}</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} placeholder="Motivazione" />
        </div>
        <DialogFooter>
          <Button data-testid={`${testid}-confirm`} onClick={() => { if (required && !reason.trim()) return setError("La motivazione è obbligatoria"); onConfirm(reason); }}
            className={`w-full rounded-lg h-11 font-semibold ${danger ? "bg-destructive hover:bg-destructive/90" : ""}`}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ entry, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/work-entries/${entry.id}/history`).then((r) => setData(r.data)); }, [entry.id]);
  const opLabel = { correzione: "Correzione", correzione_ufficio: "Correzione (Ufficio)", annullamento: "Annullamento", annullamento_ufficio: "Annullamento (Ufficio)", creazione_ufficio: "Creazione (Ufficio)" };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Cronologia della registrazione</DialogTitle>
          <DialogDescription>Sola lettura · {fmtDateIt(entry.work_date)}</DialogDescription></DialogHeader>
        {!data ? <p className="text-sm text-slate-500">Caricamento…</p> : (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm">
              <p className="font-semibold text-slate-700 mb-1">Creazione originaria</p>
              <p className="text-slate-500">{entry.start_time}–{entry.end_time}{entry.role_or_task && ` · ${entry.role_or_task}`} · creata il {new Date(entry.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}</p>
            </div>
            <StampInfo entry={entry} />
            {data.history.length === 0 && <p className="text-sm text-slate-500">Nessuna modifica registrata.</p>}
            {data.history.map((h) => (
              <div key={h.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0 text-[10px]">{opLabel[h.operation_type] || h.operation_type}</Badge>
                  <span className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}</span>
                </div>
                <p className="text-slate-600"><span className="text-slate-400">Autore:</span> {h.actor_name} ({h.actor_role})</p>
                {h.previous_data && <p className="text-slate-500 mt-1"><span className="text-slate-400">Prima:</span> {JSON.stringify(h.previous_data)}</p>}
                {h.new_data && <p className="text-slate-500"><span className="text-slate-400">Dopo:</span> {JSON.stringify(h.new_data)}</p>}
                <p className="text-slate-600 mt-1"><span className="text-slate-400">Motivazione:</span> {h.mandatory_reason || "—"}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OfficeEntryDialog({ mode, reg, entry, workerId, onClose, onSaved }) {
  const cfg = SERVICES[slugFromKey(reg.service_type)];
  const roleGated = !!cfg.roleEntity;
  const gatedRole = roleGated ? (reg.entity_kind === "appalto" ? "Ausiliario" : "Educatore") : null;
  const [date, setDate] = useState(entry ? entry.work_date : `${reg.year}-${String(reg.month).padStart(2, "0")}-01`);
  const [start, setStart] = useState(entry ? entry.start_time : "");
  const [end, setEnd] = useState(entry ? entry.end_time : "");
  const [endNextDay, setEndNextDay] = useState(false);
  const [role, setRole] = useState(entry?.role_or_task || gatedRole || "");
  const [entityId, setEntityId] = useState(reg.entity.id);
  const [entities, setEntities] = useState([]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const entKindKey = reg.entity_kind || ENTITY_KIND_FALLBACK[reg.service_type] || "operative_site";
  const entKind = ENTITY_KINDS[entKindKey] || ENTITY_KINDS.operative_site;

  useEffect(() => {
    if (mode !== "correct") return;
    api.get(entKind.adminApi()).then((r) => {
      // Per le Sedi operative mostra solo quelle associate al servizio del registro.
      const list = entKindKey === "operative_site"
        ? r.data.filter((d) => (d.service_types || []).includes(reg.service_type))
        : r.data;
      setEntities(list);
    });
  }, [mode, reg.service_type, entKindKey]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason.trim()) return setError("La motivazione è obbligatoria");
    if (cfg.roles && !role) return setError("Seleziona il ruolo/mansione");
    if (!start.includes(":") || !end.includes(":") || start.endsWith(":") || end.endsWith(":") || start.startsWith(":") || end.startsWith(":")) return setError("Seleziona ora e minuti");
    setSaving(true);
    try {
      if (mode === "create") {
        const nextDay = (iso) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
        const end_date = endNextDay ? nextDay(date) : date;
        await api.post("/office/work-entries", { worker_id: workerId, service_type: reg.service_type, work_date: date, start_time: start, end_time: end, end_date, entity_id: reg.entity.id, role_or_task: cfg.roles ? role : null, reason });
      } else {
        await api.post(`/work-entries/${entry.id}/correct`, { start_time: start, end_time: end, role_or_task: cfg.roles ? role : null, entity_id: entityId, reason });
      }
      toast.success(mode === "create" ? "Prestazione aggiunta" : "Registrazione corretta");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const entLabel = (d) => entKind.title(d);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{mode === "create" ? "Aggiungi prestazione (Ufficio)" : "Correggi prestazione (Ufficio)"}</DialogTitle>
          <DialogDescription>Ogni intervento è tracciato con motivazione e autore.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="office-entry-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <DatePicker label="Data" value={date} onChange={setDate} testid="office-entry-date" />
          <div className="grid grid-cols-2 gap-3">
            <TimePicker label="Inizio" value={start} onChange={setStart} testid="office-start" />
            <TimePicker label="Fine" value={end} onChange={setEnd} testid="office-end" />
          </div>
          {mode === "create" && (
            <label className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer" data-testid="office-endnextday-row">
              <Checkbox checked={endNextDay} onCheckedChange={(v) => setEndNextDay(!!v)} data-testid="office-end-next-day" />
              <span className="text-sm text-slate-700">Il turno termina il giorno successivo (oltre la mezzanotte)</span>
            </label>
          )}
          {cfg.roles && (
            roleGated ? (
              <div className="space-y-1.5"><Label>Ruolo</Label>
                <div data-testid="office-role-locked" className="h-11 flex items-center rounded-lg bg-slate-50 border border-slate-200 px-3 text-sm font-medium text-slate-700">{role || "—"}</div></div>
            ) : (
              <div className="space-y-1.5"><Label>{cfg.key === "centro_estivo" ? "Mansione" : "Ruolo"}</Label>
                <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="office-role-select" className="h-11"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{cfg.roles.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent></Select></div>
            )
          )}
          {mode === "correct" && !roleGated && (
            <div className="space-y-1.5"><Label>Entità associata (correggibile)</Label>
              <Select value={entityId} onValueChange={setEntityId}><SelectTrigger data-testid="office-entity-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{entities.map((d) => (<SelectItem key={d.id} value={d.id}>{entLabel(d)}</SelectItem>))}</SelectContent></Select></div>
          )}
          <div className="space-y-1.5"><Label>Motivazione</Label>
            <Textarea required value={reason} data-testid="office-entry-reason" onChange={(e) => setReason(e.target.value)} placeholder="Motivazione dell'intervento" /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="office-entry-submit" className="w-full rounded-lg h-11 font-semibold">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
